const KeyMirror = require('keymirror');
const { AUTH: Types, SIGNUP: SignupTypes } = require('../action-types');
const { getLoginContext } = require('utils/login-context');
const AuthConstants = require('services/auth-management/constants');
const MfaCooldown = require('services/auth-management/mfa-cooldown');
const BootstrapCache = require('services/auth-management/bootstrap-cache');

const AUTH_STATUSES = KeyMirror({
    INIT: true,
    WAITING: true,
    WAITING_LOGOUT: true,
    FINISHED: true
});

const internals = {};

const initialState = {
    status: AUTH_STATUSES.INIT,
    loginContext: getLoginContext(),
    mfaError: null
};

const reducer = (state = initialState, action) => {

    switch (action.type) {
        case Types.UPDATE_LOGIN_CONTEXT:
            return {
                ...state,
                loginContext: {
                    ...state.loginContext,
                    ...action.payload
                }
            };
        case Types.UPDATE_TWILIO_TOKEN.SUCCESS:

            if (!selectors.getIsAuthenticated(state)) {
                return state;
            }

            if (typeof action.payload?.result?.token !== 'string' || !action.payload.result.token.trim()) {
                return state;
            }

            return {
                ...state,
                tokens: {
                    ...state.tokens,
                    twilio: action.payload.result.token
                }
            };
        case SignupTypes.IDENTIFY.COMPLETE.SUCCESS:
            return {
                ...state,
                tokens: null
            };
        case SignupTypes.COMPLETE.SUCCESS: {
            const result = action.payload?.result || {};
            const isPendingMfa = AuthConstants.authFlow.pendingMfaStatuses.includes(result.status);

            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                tokens: internals.getTokensFromLoginResult(result) || null,
                isVerified: isPendingMfa ? false : result.isVerified !== false
            };
        }
        case Types.LOGIN.REQUEST:
            return {
                ...state,
                status: AUTH_STATUSES.WAITING,
                mfaError: null
            };
        case Types.START_MFA_SETUP.REQUEST:
        case Types.RESEND_MFA_CHALLENGE.REQUEST:
        case Types.RESET_MFA_PHONE.REQUEST:
        case Types.VERIFY_MFA.REQUEST:
        case Types.LOGIN_TOKEN.REQUEST:
        case Types.LOGIN_SSO_TOKEN.REQUEST:
            return {
                ...state,
                status: AUTH_STATUSES.WAITING,
                mfaError: null
            };
        case Types.LOGIN.SUCCESS:
        case Types.LOGIN_TOKEN.SUCCESS:
        case Types.LOGIN_SSO_TOKEN.SUCCESS:
        case Types.VERIFY_MFA.SUCCESS: {
            if (AuthConstants.authFlow.pendingMfaStatuses.includes(action.payload?.result?.status)) {
                return {
                    ...state,
                    status: AUTH_STATUSES.FINISHED,
                    tokens: null,
                    isVerified: false,
                    mfaError: null
                };
            }

            if (action.type === Types.VERIFY_MFA.SUCCESS && action.payload?.result?.status === 'verified') {
                return {
                    ...state,
                    status: AUTH_STATUSES.FINISHED,
                    isVerified: true,
                    mfaError: null
                };
            }

            const tokens = internals.getTokensFromLoginResult(action.payload.result);

            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                tokens,
                isVerified: action.type === Types.VERIFY_MFA.SUCCESS && !!tokens?.nearpeer
                    ? true
                    : action.payload.result.isVerified,
                loginContext: internals.isRestoringMfaChallenge(state, action)
                    ? state.loginContext
                    : internals.getSuccessfulLoginContext(state, action),
                mfaError: null
            };
        }
        case Types.START_MFA_SETUP.SUCCESS:
        case Types.RESEND_MFA_CHALLENGE.SUCCESS:
        case Types.RESET_MFA_PHONE.SUCCESS:
            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                mfaError: null
            };
        case Types.LOGOUT.REQUEST:
            return {
                ...state,
                status: AUTH_STATUSES.WAITING_LOGOUT,
                isVerified: false
            };
        case Types.LOGIN.FAILURE:
        case Types.LOGIN_TOKEN.FAILURE:
            BootstrapCache.clear();
            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                tokens: null
            };
        case Types.LOGOUT.SUCCESS:
        case Types.LOGOUT_SYNC:
            return internals.getLoggedOutState(state);
        case Types.CLOSE_ACCOUNT.SUCCESS:
        case Types.ACCOUNT_CLOSED_CLEAR:
            return internals.getLoggedOutState(state, {
                preserveRememberedEmail: false
            });
        case Types.START_MFA_SETUP.FAILURE:
        case Types.RESEND_MFA_CHALLENGE.FAILURE:
        case Types.RESET_MFA_PHONE.FAILURE:
        case Types.VERIFY_MFA.FAILURE:
            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                mfaError: internals.getMfaError(action)
            };
        case Types.LOGIN_SSO_TOKEN.FAILURE:
            return {
                ...state,
                status: AUTH_STATUSES.FINISHED,
                tokens: null
            };
        case Types.LOGOUT.FAILURE:
            return {
                ...state,
                status: AUTH_STATUSES.FINISHED
            };
        default:
            return state;
    }
};

const selectors = {
    AUTH_STATUSES,
    getIsAuthenticated: (state) => !!state.tokens?.nearpeer,
    getAuthStatus: (state) => state.status,
    getVerifiedStatus: (state) => state.isVerified,
    getLoginContext: (state) => state.loginContext || {},
    getMfaError: (state) => state.mfaError,
    getSSOToken: (state) => state.tokens?.sso,
    getApiToken: (state) => {

        if (!selectors.getIsAuthenticated(state)) {
            throw new Error('User is not logged-in!');
        }

        return state.tokens.nearpeer;
    },
    getTwilioToken: (state) => {

        if (!selectors.getIsAuthenticated(state)) {
            throw new Error('User is not logged-in!');
        }

        return state.tokens.twilio;
    }
};

internals.getTokensFromLoginResult = (result = {}) => {

    if (result.tokens) {
        return result.tokens;
    }

    if (!result.nearpeer && !result.twilio && !result.sso) {
        return undefined;
    }

    return {
        nearpeer: result.nearpeer,
        twilio: result.twilio,
        sso: result.sso
    };
};

internals.getMfaError = (action) => {

    const data = action.payload?.error?.response?.data;

    if (!data || typeof data !== 'object') {
        return {
            code: null,
            message: null,
            retryAfterSeconds: null
        };
    }

    return {
        code: data.code || null,
        cooldownUntil: MfaCooldown.getCooldownUntil(data),
        message: data.message || null,
        mfaToken: action.payload?.request?.mfaToken || data.mfaToken || null,
        retryAfterSeconds: Number.isFinite(Number(data.retryAfterSeconds))
            ? Number(data.retryAfterSeconds)
            : null
    };
};

internals.getSuccessfulLoginContext = (state, action) => {

    const successfulEmail = action.payload?.request?.email
        || state.loginContext?.email
        || state.loginContext?.lastLoginEmail;
    const result = action.payload?.result || {};
    const request = action.payload?.request || {};
    const nextLoginContext = {
        ...state.loginContext,
        ...(successfulEmail ? {
            email: successfulEmail,
            lastLoginEmail: successfulEmail
        } : {}),
        ...(typeof result.schoolId !== 'undefined' ? {
            schoolId: result.schoolId
        } : {}),
        ...(typeof request.sso !== 'undefined' ? {
            sso: request.sso
        } : {}),
        mfaToken: null,
        mfaMethod: null,
        mfaPurpose: null,
        mfaNeedsPhone: null,
        mfaPhone: null,
        mfaPhoneCountry: null,
        mfaCooldownCode: null,
        mfaCooldownUntil: null,
        maskedDestination: null,
        authMethod: null,
        authStatus: AuthConstants.authFlow.statuses.authenticated,
        authFlowSource: AuthConstants.authFlow.sources.loginSuccess
    };

    if (result.isVerified === false) {
        return {
            ...nextLoginContext,
            ...(typeof result.verificationToken !== 'undefined' ? {
                verificationToken: result.verificationToken
            } : {}),
            ...(typeof result.roleId !== 'undefined' ? {
                roleId: result.roleId
            } : {})
        };
    }

    return {
        ...nextLoginContext,
        ...(action.type === Types.LOGIN_SSO_TOKEN.SUCCESS && successfulEmail ? {
            forceSSOLoginPromptEmail: null,
            skipSSOLoginPromptEmail: successfulEmail
        } : {}),
        verificationToken: null,
        roleId: null
    };
};

internals.isRestoringMfaChallenge = (state, action) => action.type === Types.LOGIN.SUCCESS
    && !!action.payload?.request?.token
    && !!state.loginContext?.mfaToken;

internals.getLoggedOutState = (state, { preserveRememberedEmail = true } = {}) => {

    BootstrapCache.clear();

    const rememberedEmail = preserveRememberedEmail
        ? state.loginContext?.lastLoginEmail || null
        : null;

    return {
        ...state,
        status: AUTH_STATUSES.FINISHED,
        tokens: null,
        isVerified: false,
        mfaError: null,
        loginContext: {
            ...AuthConstants.loginContext.runtimeResetFields,
            email: rememberedEmail,
            lastLoginEmail: rememberedEmail
        }
    };
};

module.exports = Object.assign(reducer, { selectors });
